// noinspection SpellCheckingInspection

import React, {useEffect, useRef, useState, useMemo, CSSProperties} from "react"
import {
    getEvents,
    getUsers,
    getVehicles, getWorkOrders, postDriverTimesheet,
    postTimesheet,
    updateDriverTimeEntry,
    updateTimeEntry
} from "../api/dataService"
import {
    DriverTimeSheet,
    InitialDriverTimesheetObject,
    PartialDriverTimeSheet,
    PartialTimeSheet,
    PartialUser,
    PartialWorkEvent, UserRoles, Vehicle,
    WorkOrder
} from "../types"
import {checkIsAllowed, handlePostError} from "../util/Helpers"
import {Alert, Button, Col, Form, InputGroup, Row} from "react-bootstrap"
import * as Yup from 'yup'
import {ErrorMessage, Field, Formik} from "formik"
import Container from "react-bootstrap/Container"
import Select, { GroupBase } from "react-select"
import AsyncSelect from "react-select/async"
import {format} from "date-fns"
import DatePicker from "react-datepicker"
import {useSelector} from "react-redux"
import {RootState} from "../redux/reduxTypes"
import {Navigate, useNavigate} from "react-router-dom"


interface addTimeProps {
    formik: any,
    eventSelectRef: any
    employeeSelectRef: any
    props: CreateTimeEntryProps
    allowedToUpdate: boolean
    isUpdating: boolean
    employeeList: PartialUser[]
    eventList: PartialWorkEvent[]
    vehicleList: Vehicle[]
    disableSubmit?: boolean
    workOrderList: WorkOrder[]
    selectedWorkOrder: WorkOrder | null
    setSelectedWorkOrder: (order: WorkOrder | null) => void
}

// Helper for determining event labels.
const getWorkOrderIdFromEvent = (ev: PartialWorkEvent): string => {
    const wo = ev.work_order;

    if (!wo) return '';

    if (typeof wo === 'object' && wo !== null) {
        return String((wo as WorkOrder).id ?? '');
    }

    return String(wo);
};

type EventSelectOption = { label: string; value: string | number };
type EventGroup = GroupBase<EventSelectOption>;

const formatEventLabel = (ev: PartialWorkEvent): string => {
    const woId = getWorkOrderIdFromEvent(ev);
    // "work order id - event name"
    return `${woId}${woId ? ' - ' : ''}${ev.event_name ?? ''}`.trim();
};

const toEventOption = (ev: PartialWorkEvent): EventSelectOption => ({
    label: formatEventLabel(ev),
    value: ev.id as string | number,
});

const groupStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
};
const groupBadgeStyles: CSSProperties = {
    backgroundColor: '#EBECF0',
    borderRadius: '2em',
    color: '#172B4D',
    display: 'inline-block',
    fontSize: 12,
    fontWeight: 'normal',
    lineHeight: '1',
    minWidth: 1,
    padding: '0.16666666666667em 0.5em',
    textAlign: 'center',
};

const formatGroupLabel = (data: EventGroup) => (
    <div style={groupStyles}>
        <span>{data.label ?? 'Other'}</span>
        <span style={groupBadgeStyles}>{data.options.length}</span>
    </div>
);

// Custom input for DatePicker: readOnly prevents the mobile keyboard from appearing
// while react-datepicker still injects its own onClick to open the calendar.
const ReadOnlyDateInput = React.forwardRef<HTMLInputElement, React.HTMLProps<HTMLInputElement>>(
    (props, ref) => <input {...props} readOnly ref={ref} />
);

const getEventDateInfo = (ev: PartialWorkEvent): { key: string; label: string; time: number | null } => {
    if (!ev.start_date) {
        return { key: 'no-date', label: 'No Date', time: null };
    }
    const date = new Date(ev.start_date);
    if (isNaN(date.getTime())) {
        return { key: 'no-date', label: 'No Date', time: null };
    }
    return {
        key: format(date, 'yyyy-MM-dd'),
        label: format(date, 'EEE, MMM d, yyyy'),
        time: date.getTime(),
    };
};

const buildGroupedEventOptions = (
    events: PartialWorkEvent[],
    selectedWorkOrder: WorkOrder | null,
    inputValue?: string
): EventGroup[] => {
    const normalizedInput = inputValue?.toLowerCase().trim() ?? '';

    const groups = new Map<string, { label: string; items: PartialWorkEvent[]; time: number | null }>();

    events.forEach((ev) => {
        if (ev.id === undefined || ev.id === null) return;
        if (selectedWorkOrder) {
            const woId = getWorkOrderIdFromEvent(ev);
            if (String(woId) !== String(selectedWorkOrder.id)) return;
        }

        if (normalizedInput) {
            const label = formatEventLabel(ev).toLowerCase();
            if (!label.includes(normalizedInput)) return;
        }

        const { key, label, time } = getEventDateInfo(ev);
        const groupTime = time === null ? null : new Date(time).setHours(0, 0, 0, 0);
        const existing = groups.get(key);
        if (existing) {
            existing.items.push(ev);
        } else {
            groups.set(key, { label, items: [ev], time: groupTime });
        }
    });

    const getSortableTime = (value?: string): number | null => {
        if (!value) return null;
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d.getTime();
    };

    const sortedGroups = Array.from(groups.entries()).map(([key, value]) => {
        const options = value.items
            .slice()
            .sort((a, b) => {
                const aStart = getSortableTime(a.start_date);
                const bStart = getSortableTime(b.start_date);
                const aStartTime = aStart ?? Number.MAX_SAFE_INTEGER;
                const bStartTime = bStart ?? Number.MAX_SAFE_INTEGER;
                if (aStartTime !== bStartTime) return aStartTime - bStartTime;

                const aEnd = getSortableTime(a.end_date);
                const bEnd = getSortableTime(b.end_date);
                const aEndTime = aEnd ?? Number.MAX_SAFE_INTEGER;
                const bEndTime = bEnd ?? Number.MAX_SAFE_INTEGER;
                if (aEndTime !== bEndTime) return aEndTime - bEndTime;

                return formatEventLabel(a).localeCompare(formatEventLabel(b));
            })
            .map(toEventOption);
        return {
            key,
            label: value.label,
            time: value.time,
            options,
        };
    });

    sortedGroups.sort((a, b) => {
        if (a.time === null && b.time === null) return a.label.localeCompare(b.label);
        if (a.time === null) return -1;
        if (b.time === null) return 1;
        if (a.time !== b.time) return b.time - a.time;
        return a.label.localeCompare(b.label);
    });

    return sortedGroups.map(({ label, options }) => ({ label, options }));
};

const getWorkOrderId = (wo: any): string | null => {
    if (!wo) return null;
    if (typeof wo === 'string' || typeof wo === 'number') return String(wo);
    if (typeof wo === 'object') {
        if ('id' in wo && wo.id != null) return String(wo.id);
    }
    return null;
};

const AddTime: React.FC<addTimeProps> = ({
                                             formik,
                                             props,
                                             employeeSelectRef, // (unused but kept to match signature)
                                             employeeList,
                                             eventSelectRef, // (unused but kept to match signature)
                                             allowedToUpdate,
                                             isUpdating,
                                             eventList,
                                             disableSubmit = false,
                                             workOrderList,
                                             selectedWorkOrder,
                                             setSelectedWorkOrder,
                                         }) => {
    // keeps preselected events showing properly based on Formik’s stored ID.
    const selectedEventOption = useMemo(() => {
        const eventId = formik.values.event;
        if (!eventId) return null;

        const ev = eventList.find((e) => String(e.id) === String(eventId));
        if (!ev) return null;

        return toEventOption(ev);
    }, [formik.values.event, eventList]);

    const groupedEventOptions = useMemo(
        () => buildGroupedEventOptions(eventList, selectedWorkOrder),
        [eventList, selectedWorkOrder]
    );

    const loadGroupedEventOptions = (inputValue: string) =>
        Promise.resolve(buildGroupedEventOptions(eventList, selectedWorkOrder, inputValue));

    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    const [localWorkDate, setLocalWorkDate] = useState<Date | null>(null);

    // ---- Date/Time helpers (UI-only; Formik still stores start_date/end_date) ----

    const parseToDateOrNull = (value: any): Date | null => {
        if (!value) return null;
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
    };

    const buildDateTimeString = (baseDate: Date, timeSource: Date) => {
        const combined = new Date(baseDate);
        combined.setHours(timeSource.getHours(), timeSource.getMinutes(), 0, 0);
        return format(combined, "yyyy-MM-dd HH:mm:ss");
    };

    const getSelectedWorkDate = (): Date => {
        // Priority:
        // 1) existing start_date (update case)
        // 2) existing end_date (fallback)
        // 3) localWorkDate (user changed date picker before entering times)
        // 4) props.defaultWorkDate (from ManageTimeEntriesPage)
        // 5) today (create case)
        const start = parseToDateOrNull(formik.values.start_date);
        if (start) return new Date(start.getFullYear(), start.getMonth(), start.getDate());

        const end = parseToDateOrNull(formik.values.end_date);
        if (end) return new Date(end.getFullYear(), end.getMonth(), end.getDate());

        if (localWorkDate) return localWorkDate;

        if (props.defaultWorkDate) {
            const d = props.defaultWorkDate;
            return new Date(d.getFullYear(), d.getMonth(), d.getDate());
        }

        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    };

    const workDate = getSelectedWorkDate();

    const startTimeDate = parseToDateOrNull(formik.values.start_date);

    const endTimeDate = parseToDateOrNull(formik.values.end_date);

    const handleWorkDateChange = (date: Date | null) => {
        if (!date) return;

        const currentStart = parseToDateOrNull(formik.values.start_date);
        const currentEnd = parseToDateOrNull(formik.values.end_date);

        if (!currentStart) {
            // No start time yet — store the override so the picker doesn't snap back
            setLocalWorkDate(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
            return;
        }

        const startStr = buildDateTimeString(date, currentStart);
        formik.setFieldValue("start_date", startStr);

        if (currentEnd) {
            const endStr = buildDateTimeString(date, currentEnd);
            if (new Date(endStr) <= new Date(startStr)) {
                formik.setFieldValue("end_date", "");
            } else {
                formik.setFieldValue("end_date", endStr);
            }
        }
    };


    const handleStartTimeChange = (time: Date | null) => {
        if (!time) return;

        const startStr = buildDateTimeString(workDate, time);
        formik.setFieldValue("start_date", startStr);

        // If end exists and new start is later than end, clear end
        const end = parseToDateOrNull(formik.values.end_date);
        if (end && new Date(startStr) >= end) {
            formik.setFieldValue("end_date", "");
        }
    };


    const handleEndTimeChange = (time: Date | null) => {
        if (!time) return;

        // Require start_date before allowing end_date to be set meaningfully
        if (!formik.values.start_date) return;

        const endStr = buildDateTimeString(workDate, time);

        // Enforce end > start
        const start = parseToDateOrNull(formik.values.start_date);
        if (start && new Date(endStr) <= start) {
            // Keep it simple: clear end if invalid
            formik.setFieldValue("end_date", "");
            return;
        }

        formik.setFieldValue("end_date", endStr);
    };

    return (
        <>
            {allowedToUpdate && (
                <Row className="mb-3">
                    <Col xs={12}>
                        <Form.Label className={"fw-bold"}>
                            Employee <span className="text-danger">*</span>
                        </Form.Label>
                        <AsyncSelect
                            cacheOptions
                            defaultOptions={employeeList.map((user) => ({
                                label: `${user.first_name} ${user.last_name}`,
                                value: user.id,
                            }))}
                            name="employee"
                            placeholder="Search employee..."
                            loadOptions={(inputValue) =>
                                Promise.resolve(
                                    employeeList
                                        .map((user) => ({
                                            label: `${user.first_name} ${user.last_name}`,
                                            value: user.id,
                                        }))
                                        .filter((option) =>
                                            option.label.toLowerCase().includes(inputValue.toLowerCase())
                                        )
                                )
                            }
                            isLoading={!employeeList.length}
                            value={(() => {
                                const employee = employeeList.find((u) => u.id === formik.values.employee);
                                return employee
                                    ? {label: `${employee.first_name} ${employee.last_name}`, value: employee.id}
                                    : null;
                            })()}
                            onChange={(selectedOption) => {
                                formik.setFieldValue("employee", selectedOption ? selectedOption.value : "");
                            }}
                            isDisabled={!!props.timeEntryToUpdate?.employee}
                        />
                        <ErrorMessage name="employee" component="div" className="text-danger"/>
                    </Col>
                </Row>
            )}

            <Row className="mb-3">
                <Col xs={12} className="mb-3">
                    <Form.Label className={"fw-bold"}>Work Order</Form.Label>
                    <AsyncSelect
                        cacheOptions
                        defaultOptions={workOrderList.map((wo) => ({
                            label: `WO#${wo.id} - ${wo.billing_client}`,
                            value: wo.id,
                        }))}
                        loadOptions={(inputValue) =>
                            Promise.resolve(
                                workOrderList
                                    .map((wo) => ({
                                        label: `WO#${wo.id} - ${wo.billing_client}`,
                                        value: wo.id,
                                    }))
                                    .filter((option) =>
                                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                                    )
                            )
                        }
                        isLoading={!workOrderList.length}
                        value={
                            selectedWorkOrder
                                ? {
                                    label: `WO#${selectedWorkOrder.id} - ${selectedWorkOrder.billing_client}`,
                                    value: selectedWorkOrder.id,
                                }
                                : null
                        }
                        onChange={(option) => {
                            if (!option) {
                                setSelectedWorkOrder(null);
                                formik.setFieldValue("event", "");
                                formik.setFieldTouched("event", false, false);
                                return;
                            }

                            const wo = workOrderList.find((w) => w.id === option.value) || null;
                            if (wo?.id !== selectedWorkOrder?.id) {
                                setSelectedWorkOrder(wo);
                                formik.setFieldValue("event", "");
                                formik.setFieldTouched("event", false, false);
                            }
                        }}
                        isDisabled={!!(props.timeEntryToUpdate?.event || props.selectedEvent)}
                        isClearable={true}
                        placeholder="Select Work Order"
                    />
                </Col>

                <Col xs={12} className="mb-3">
                    <Form.Label className={"fw-bold"}>
                        Event <span className="text-danger">*</span>
                    </Form.Label>
                    <AsyncSelect<EventSelectOption, false, EventGroup>
                        cacheOptions
                        defaultOptions={groupedEventOptions}
                        loadOptions={loadGroupedEventOptions}
                        key={selectedWorkOrder?.id ?? "no-wo"}
                        isLoading={!eventList.length}
                        value={selectedEventOption}
                        onChange={(selectedOption) => {
                            formik.setFieldValue("event", selectedOption ? selectedOption.value : "");
                        }}
                        isDisabled={!!props.selectedEvent || !!props.timeEntryToUpdate?.event}
                        formatGroupLabel={formatGroupLabel}
                        placeholder="Select Event"
                    />
                    <ErrorMessage name="event" component="div" className="text-danger"/>
                </Col>
            </Row>

            {/* Date row */}
            <Row className="mb-4 justify-content-center">
                <Col xs={12}>
                    <Form.Group controlId="work_date" className="text-center">
                        <Form.Label className="fw-bold d-block">
                            Date <span className="text-danger">*</span>
                        </Form.Label>

                        <div className="w-100">
                            <DatePicker
                                wrapperClassName="w-100"
                                dateFormat="dd-MM-yy"
                                selected={workDate}
                                onChange={handleWorkDateChange}
                                placeholderText="Select date"
                                maxDate={today}
                                customInput={<ReadOnlyDateInput className="form-control text-center w-100" />}
                            />
                        </div>
                    </Form.Group>
                </Col>
            </Row>

            {/* Time row */}
            <Row className="mb-4 g-3">
                <Col xs={6}>
                    <Form.Group controlId="start_time" className="text-center">
                        <Form.Label className="fw-bold d-block" style={{whiteSpace: 'nowrap'}}>
                            Start Time <span className="text-danger">*</span>
                        </Form.Label>

                        <div className="w-100">
                            <DatePicker
                                className="form-control text-center w-100"
                                selected={startTimeDate}
                                onChange={handleStartTimeChange}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={15}
                                dateFormat="h:mm aa"
                                placeholderText="Start time"
                            />
                        </div>

                        <ErrorMessage name="start_date" component="div" className="text-danger" />
                    </Form.Group>
                </Col>

                <Col xs={6}>
                    <Form.Group controlId="end_time" className="text-center">
                        <Form.Label className="fw-bold d-block" style={{whiteSpace: 'nowrap'}}>
                            End Time <span className="text-danger">*</span>
                        </Form.Label>

                        <div className="w-100">
                            <DatePicker
                                className="form-control text-center w-100"
                                selected={formik.values.end_date ? endTimeDate : null}
                                onChange={handleEndTimeChange}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={15}
                                dateFormat="h:mm aa"
                                placeholderText="End time"
                                disabled={!formik.values.start_date}
                                filterTime={(time: Date) => {
                                    if (!formik.values.start_date) return false;

                                    const start = parseToDateOrNull(formik.values.start_date);
                                    if (!start) return false;

                                    const candidate = new Date(workDate);
                                    candidate.setHours(time.getHours(), time.getMinutes(), 0, 0);

                                    const startOnWorkDate = new Date(workDate);
                                    startOnWorkDate.setHours(start.getHours(), start.getMinutes(), 0, 0);

                                    return candidate.getTime() > startOnWorkDate.getTime();
                                }}
                            />
                        </div>

                        <ErrorMessage name="end_date" component="div" className="text-danger" />
                    </Form.Group>
                </Col>
            </Row>

            <Row className={"mb-3"}>
                <Form.Label className={"fw-bold"}>Shift Notes:</Form.Label>
                <Field
                    style={{height: "100px"}}
                    name={"shift_note"}
                    component={"textarea"}
                    className={"form-control"}
                    placeholder={"Add Notes Here"}
                    as={Form.Control}
                />
            </Row>

            {!disableSubmit && (
                <Row className={"justify-content-center"}>
                    <Col xs={12} sm={"auto"} className="d-grid">
                        <Button variant="primary" type="submit" disabled={formik.isSubmitting}>
                            {isUpdating ? "Update" : "Submit"}
                        </Button>
                    </Col>
                </Row>
            )}
        </>
    );
};

const DriverAddTime: React.FC<addTimeProps> = ({
                                                   formik,
                                                   props,
                                                   employeeSelectRef,
                                                   employeeList,
                                                   eventSelectRef,
                                                   allowedToUpdate,
                                                   isUpdating,
                                                   eventList,
                                                   vehicleList,
                                                   workOrderList,
                                                   selectedWorkOrder,
                                                   setSelectedWorkOrder
                                               }) => {

    const vehicleSelectRef = useRef<any>(null)
    const [toDestHour, setToDestHour] = useState<string>(
        props.driverTimeEntryToUpdate?.drive_time_to_location ? props.driverTimeEntryToUpdate.drive_time_to_location.split(":")[0] : '00'
    )
    const [toDestMin, setToDestMin] = useState<string>(
        props.driverTimeEntryToUpdate?.drive_time_to_location ? props.driverTimeEntryToUpdate.drive_time_to_location.split(":")[1] : '00'
    )
    const [fromDestHour, setFromDestHour] = useState(
        props.driverTimeEntryToUpdate?.drive_time_from_location ? props.driverTimeEntryToUpdate.drive_time_from_location.split(":")[0] : '00'
    )
    const [fromDestMin, setFromDestMin] = useState(
        props.driverTimeEntryToUpdate?.drive_time_from_location ? props.driverTimeEntryToUpdate.drive_time_from_location.split(":")[1] : '00'
    )
    const [offTruckHour, setOffTruckHour] = useState(
        props.driverTimeEntryToUpdate?.off_truck_time ? props.driverTimeEntryToUpdate.off_truck_time.split(":")[0] : '00'
    )
    const [offTruckMin, setOffTruckMin] = useState(
        props.driverTimeEntryToUpdate?.off_truck_time ? props.driverTimeEntryToUpdate.off_truck_time.split(":")[1] : '00'
    )

    const normalizeTimePart = (rawValue: string, max: number) => {
        const parsed = Number.parseInt(rawValue, 10)
        if (Number.isNaN(parsed) || parsed < 0) return '00'
        if (parsed > max) return max.toString()
        return parsed.toString()
    }

    useEffect(() => {
        const newTime = `${toDestHour.padStart(2, '0')}:${toDestMin.padStart(2, '0')}`
        formik.setFieldValue('drive_time_to_location', newTime)
    }, [toDestHour, toDestMin]);

    useEffect(() => {
        const newTime = `${fromDestHour.padStart(2, '0')}:${fromDestMin.padStart(2, '0')}`
        formik.setFieldValue('drive_time_from_location', newTime)
    }, [fromDestHour, fromDestMin]);

    useEffect(() => {
        const newTime = `${offTruckHour.padStart(2, '0')}:${offTruckMin.padStart(2, '0')}`
        formik.setFieldValue('off_truck_time', newTime)
    }, [offTruckHour, offTruckMin]);

    return (
        <>
            <Row>
                {AddTime({
                    formik: formik,
                    eventSelectRef: eventSelectRef,
                    employeeSelectRef: employeeSelectRef,
                    props: props,
                    allowedToUpdate: allowedToUpdate,
                    isUpdating: isUpdating,
                    employeeList: employeeList,
                    eventList: eventList,
                    disableSubmit: true,
                    vehicleList: [],
                    workOrderList: workOrderList,
                    selectedWorkOrder: selectedWorkOrder,
                    setSelectedWorkOrder: setSelectedWorkOrder
                })}
            </Row>

            <hr className="my-3" />
            <p className="fw-bold mb-3">Vehicle & Drive Times</p>

            <Row className={"mb-3"}>
                <Form.Group as={Col} controlId="vehicleSelect">
                    <Form.Label>Vehicle <span className="text-danger">*</span></Form.Label>
                    <Select
                        ref={vehicleSelectRef}
                        name="vehicle"
                        defaultValue={
                            props.driverTimeEntryToUpdate?.vehicle ? (
                                {
                                    label: `${props.driverTimeEntryToUpdate.vehicle.make} ${props.driverTimeEntryToUpdate.vehicle.model} - ${props.driverTimeEntryToUpdate.vehicle.vin}`,
                                    value: props.driverTimeEntryToUpdate.vehicle.id
                                }
                            ) : undefined}
                        options={vehicleList.map(entry => ({
                            label: `${entry.make} ${entry.model} - ${entry.vin}`,
                            value: entry.id,
                        })).sort((a, b) => a.label.localeCompare(b.label))}
                        isClearable={false}
                        onChange={(selectedOptions) => {
                            formik.setFieldValue('vehicle', selectedOptions ? selectedOptions.value : '')
                        }}
                        isDisabled={!!(props.driverTimeEntryToUpdate?.vehicle)}
                    />
                    <ErrorMessage name="vehicle" component="div" className="text-danger"/>
                </Form.Group>
            </Row>
            <Row className={"mb-3"}>
                <Row>
                    <Form.Label>Drive Time To Location</Form.Label>
                </Row>
                <Row>
                    <Col>
                        <InputGroup className="mb-3">
                            <Form.Control
                                type="number"
                                min={0}
                                max={23}
                                step={1}
                                id="to_hours"
                                name="to_hours"
                                aria-describedby="to_hours_label"
                                value={toDestHour}
                                onChange={(e) => {
                                    setToDestHour(normalizeTimePart(e.target.value, 23))
                                }}
                            />
                            <InputGroup.Text id="to_hours_label">Hours</InputGroup.Text>
                        </InputGroup>
                    </Col>
                    <Col>
                        <InputGroup className="mb-3">
                            <Form.Control
                                id="to_minutes"
                                name="to_minutes"
                                aria-describedby="to_minutes_label"
                                type="number"
                                min={0}
                                max={59}
                                step={1}
                                value={toDestMin}
                                onChange={(e) => {
                                    setToDestMin(normalizeTimePart(e.target.value, 59))
                                }}
                            />
                            <InputGroup.Text id="to_minutes_label">Minutes</InputGroup.Text>
                        </InputGroup>
                    </Col>
                </Row>
            </Row>
            <Row className={"mb-3"}>
                <Row>
                    <Form.Label>Drive Time From Location</Form.Label>
                </Row>
                <Row>
                    <Col>
                        <InputGroup className="mb-3">
                            <Form.Control
                                id="from_hours"
                                name="from_hours"
                                aria-describedby="from_hours_label"
                                type="number"
                                min={0}
                                max={23}
                                step={1}
                                value={fromDestHour}
                                onChange={(e) => {
                                    setFromDestHour(normalizeTimePart(e.target.value, 23))
                                }}
                            />
                            <InputGroup.Text id="from_hours_label">Hours</InputGroup.Text>
                        </InputGroup>
                    </Col>
                    <Col>
                        <InputGroup className="mb-3">
                            <Form.Control
                                id="from_minutes"
                                name="from_minutes"
                                aria-describedby="from_minutes_label"
                                type="number"
                                min={0}
                                max={59}
                                step={1}
                                value={fromDestMin}
                                onChange={(e) => {
                                    setFromDestMin(normalizeTimePart(e.target.value, 59))
                                }}
                            />
                            <InputGroup.Text id="from_minutes_label">Minutes</InputGroup.Text>
                        </InputGroup>
                    </Col>
                </Row>
            </Row>
            <Row className={"mb-3"}>
                <Row>
                    <Form.Label>Time Off Truck</Form.Label>
                </Row>
                <Row>
                    <Col>
                        <InputGroup className="mb-3">
                            <Form.Control
                                id="off_hours"
                                name="off_hours"
                                aria-describedby="off_hours_label"
                                type="number"
                                min={0}
                                max={23}
                                step={1}
                                value={offTruckHour}
                                onChange={(e) => {
                                    setOffTruckHour(normalizeTimePart(e.target.value, 23))
                                }}
                            />
                            <InputGroup.Text id="off_hours_label">Hours</InputGroup.Text>
                        </InputGroup>
                    </Col>
                    <Col>
                        <InputGroup className="mb-3">
                            <Form.Control
                                id="off_minutes"
                                name="off_minutes"
                                aria-describedby="off_minutes_label"
                                type="number"
                                min={0}
                                max={59}
                                step={1}
                                value={offTruckMin}
                                onChange={(e) => {
                                    setOffTruckMin(normalizeTimePart(e.target.value, 59))
                                }}
                            />
                            <InputGroup.Text id="off_minutes_label">Minutes</InputGroup.Text>
                        </InputGroup>
                    </Col>
                </Row>
            </Row>

            <Row className={'justify-content-center mt-3 mb-3'}>
                <Col xs={12} sm={'auto'} className="d-grid">
                    <Button variant="primary" type="submit" disabled={formik.isSubmitting}>
                        {isUpdating ? 'Update' : 'Submit'}
                    </Button>
                </Col>
            </Row>
        </>
    )
}

interface CreateTimeEntryProps {
    selectedEmployee?: PartialUser
    selectedEvent?: PartialWorkEvent
    timeEntryToUpdate?: PartialTimeSheet
    driverTimeEntryToUpdate?: PartialDriverTimeSheet
    closeModal?: (values?: PartialTimeSheet) => void
    onCreate?: (createdObject: PartialTimeSheet) => void
    onUpdate?: (updatedObject: any) => void
    renderDriver?: boolean
    defaultWorkDate?: Date
}

type TimeEntryFormValues =
    Omit<PartialTimeSheet, 'employee' | 'event'> & {
    employee: string | number | '';
    event: string | number | '';
};

type DriverTimeEntryFormValues =
    Omit<PartialDriverTimeSheet, 'employee' | 'event' | 'vehicle'> & {
    employee: string | number | '';
    event: string | number | '';
    vehicle: string | number | '';
};

type FormValues = TimeEntryFormValues | DriverTimeEntryFormValues;

const CreateTimeEntry: React.FC<CreateTimeEntryProps> = (props) => {
    const user = useSelector((state: RootState) => state.user)
    const navigate = useNavigate()
    const allowedToUpdate = (checkIsAllowed(user.groups, [UserRoles.ownership, UserRoles.management, UserRoles.officeAdmin]))
    const renderDriver = (props.renderDriver || props.driverTimeEntryToUpdate)
    const isUpdating = !!(props.timeEntryToUpdate || props.driverTimeEntryToUpdate)
    const [errorMessages, setErrorMessages] = useState<string[]>([])
    const [showSuccess, setShowSuccess] = useState(false)
    const formikRef = useRef<any>(null)
    const employeeSelectRef = useRef<any>(null)
    const eventSelectRef = useRef<any>(null)
    const [employeeList, setEmployeeList] = useState<PartialUser[]>([])
    const [eventList, setEventList] = useState<PartialWorkEvent[]>([])
    const [vehicleList, setVehicleList] = useState<Vehicle[]>([])
    const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrder | null>(null);
    const [workOrderList, setWorkOrderList] = useState<WorkOrder[]>([]);

    const endDateAfterStart = Yup.date()
        .required('End Date is required.')
        .test(
            'end-after-start',
            'End time must be after start time.',
            function (endVal) {
                const { start_date } = this.parent;
                if (!start_date || !endVal) return true; // let required() handle missing values
                return new Date(endVal) > new Date(start_date);
            }
        );

    const validationSchema =
        renderDriver ? Yup.object().shape({
                start_date: Yup.date().required('Start Date is required.'),
                end_date: endDateAfterStart,
                employee: Yup.string().required('A time entry must be associated with an employee.'),
                event: Yup.string().required('A time entry must be associated with an event'),
                vehicle: Yup.string().required('A vehicle must be selected.')
            }) :
            Yup.object().shape({
                start_date: Yup.date().required('Start Date is required.'),
                end_date: endDateAfterStart,
                employee: Yup.string().required('A time entry must be associated with an employee.'),
                event: Yup.string().required('A time entry must be associated with an event'),
            })

    const initialValues: FormValues = useMemo(() => {
        if (props.driverTimeEntryToUpdate) {
            return {
                ...props.driverTimeEntryToUpdate,
                employee: props.driverTimeEntryToUpdate.employee?.id ?? '',
                event: props.driverTimeEntryToUpdate.event?.id ?? '',
                vehicle: props.driverTimeEntryToUpdate.vehicle?.id ?? '',
            } as DriverTimeEntryFormValues;
        }

        if (props.timeEntryToUpdate) {
            return {
                ...props.timeEntryToUpdate,
                employee: props.timeEntryToUpdate.employee?.id ?? '',
                event: props.timeEntryToUpdate.event?.id ?? '',
            } as TimeEntryFormValues;
        }

        if (renderDriver) {
            return {
                ...InitialDriverTimesheetObject,
                employee: props.selectedEmployee?.id ?? '',
                event: props.selectedEvent?.id ?? '',
                vehicle: '',
            } as DriverTimeEntryFormValues;
        }

        return {
            shift_note: '',
            start_date: '',
            end_date: '',
            employee: props.selectedEmployee?.id ?? '',
            event: props.selectedEvent?.id ?? '',
        } as TimeEntryFormValues;
    }, [
        props.driverTimeEntryToUpdate,
        props.timeEntryToUpdate,
        props.selectedEmployee?.id,
        props.selectedEvent?.id,
        renderDriver,
    ]);

    const [redirectPath, setRedirectPath] = useState<string | null>(null);

    useEffect(() => {
        if (allowedToUpdate) {
            Promise.all([
                getUsers(undefined, '?is_active=true'),
                getEvents(undefined, '?workorder_status=open'),
                getWorkOrders(undefined, '?workorder_status=active')
            ]).then(
                ([userResponse, eventResponse, workOrderResponse]) => {
                    if ('redirectTo' in userResponse) {
                        setRedirectPath(userResponse.redirectTo);
                        return;
                    }
                    setEmployeeList(Array.isArray(userResponse) ? userResponse : []);

                    if ('redirectTo' in eventResponse) {
                        setRedirectPath(eventResponse.redirectTo);
                        return;
                    }
                    setEventList(Array.isArray(eventResponse) ? eventResponse : []);

                    if ('redirectTo' in workOrderResponse) {
                        setRedirectPath(workOrderResponse.redirectTo);
                        return;
                    }
                    setWorkOrderList(Array.isArray(workOrderResponse) ? workOrderResponse : []);
                })
                .catch(error => {
                    console.error("Error Fetching Data: ", error)
                })
        } else {
            if (user && user.userid && user.username)
                Promise.all([getUsers(user.userid), getEvents(undefined, `?assigned_to_username=${user.username}&status=in+progress`), getWorkOrders(undefined, '?workorder_status=active')])
                    .then(([activeUserResponse, eventResponse, workOrderResponse]) => {
                        if ('redirectTo' in activeUserResponse) {
                            setRedirectPath(activeUserResponse.redirectTo);
                            return;
                        }
                        if ('redirectTo' in eventResponse) {
                            setRedirectPath(eventResponse.redirectTo);
                            return;
                        }
                        if ('redirectTo' in workOrderResponse) {
                            setRedirectPath(workOrderResponse.redirectTo);
                            return;
                        } else {
                            if (!Array.isArray(activeUserResponse)) {
                                setEmployeeList([activeUserResponse])
                                if (formikRef.current) {
                                    formikRef.current.setFieldValue('employee', activeUserResponse.id)
                                }
                                setEventList(Array.isArray(eventResponse) ? eventResponse : []);
                                if (Array.isArray(workOrderResponse)) {
                                    setWorkOrderList(Array.isArray(workOrderResponse) ? workOrderResponse : []);
                                }
                            }
                        }
                    })
        }
    }, [allowedToUpdate, user])

    useEffect(() => {
        if (renderDriver) {
            Promise.all([getVehicles()])
                .then(([vehicleResponse]) => {
                    if ('redirectTo' in vehicleResponse) {
                        setRedirectPath(vehicleResponse.redirectTo);
                        return;
                    }
                    setVehicleList(Array.isArray(vehicleResponse) ? vehicleResponse : [])
                })
        }
    }, [renderDriver])

    useEffect(() => {
        if (props.selectedEmployee && formikRef.current) {
            formikRef.current.setFieldValue('employee', props.selectedEmployee.id)
        }
        if (props.selectedEvent && formikRef.current) {
            formikRef.current.setFieldValue('event', props.selectedEvent.id)
        }
    }, [props.selectedEmployee, props.selectedEvent]);

    useEffect(() => {
        if (isUpdating) {
            if (props.timeEntryToUpdate?.employee?.id && formikRef.current) {
                formikRef.current.setFieldValue('employee', props.timeEntryToUpdate.employee.id);
            }

            if (props.timeEntryToUpdate?.event?.id && formikRef.current) {
                formikRef.current.setFieldValue('event', props.timeEntryToUpdate.event.id);
            }
        }
    }, [
        isUpdating,
        props.timeEntryToUpdate?.event?.id,
        props.timeEntryToUpdate?.employee?.id,
    ]);

    useEffect(() => {
        if (isUpdating && renderDriver && props.driverTimeEntryToUpdate?.vehicle?.id && formikRef.current) {
            formikRef.current.setFieldValue('vehicle', props.driverTimeEntryToUpdate.vehicle.id);
        }
    }, [
        isUpdating,
        renderDriver,
        props.driverTimeEntryToUpdate?.vehicle?.id,
    ]);

    useEffect(() => {
        if (isUpdating && workOrderList.length > 0) {
            const workOrderFromEvent =
                props.timeEntryToUpdate?.event?.work_order ||
                props.driverTimeEntryToUpdate?.event?.work_order;

            const workOrderId = getWorkOrderId(workOrderFromEvent)
            if (!workOrderId) return;

            const matchedWorkOrder = workOrderList.find(workOrder => String(workOrder.id) === workOrderId) || null
            setSelectedWorkOrder(matchedWorkOrder)
        }
    }, [isUpdating, workOrderList, props.timeEntryToUpdate?.event?.work_order, props.driverTimeEntryToUpdate?.event?.work_order]);

    useEffect(() => {
        if (!props.selectedEvent || workOrderList.length === 0) return;

        const workOrderId = getWorkOrderId(props.selectedEvent.work_order)
        if (!workOrderId) return;

        const fullWorkOrder = workOrderList.find(workOrder => String(workOrder.id) === workOrderId) || null
        setSelectedWorkOrder(fullWorkOrder)
    }, [props.selectedEvent, workOrderList]);

    const handleSubmit = async (values: FormValues) => {
        try {
            let response: any = undefined

            if (isUpdating) {
                let data: any = values
                data.employee = values.employee;
                data.event = values.event;
                if (renderDriver) data.vehicle = (values as DriverTimeEntryFormValues).vehicle;

                response = renderDriver
                    ? await updateDriverTimeEntry(props.driverTimeEntryToUpdate?.id ? props.driverTimeEntryToUpdate.id.toString() : '', data)
                    : await updateTimeEntry(props.timeEntryToUpdate?.id ? props.timeEntryToUpdate.id.toString() : '', data)
            } else {
                let data: any = values;
                data.employee = values.employee;
                data.event = values.event;
                if (renderDriver) data.vehicle = (values as DriverTimeEntryFormValues).vehicle;

                response = renderDriver
                    ? await postDriverTimesheet(data)
                    : await postTimesheet(data);
            }

            if (response && !Array.isArray(response)) {
                setErrorMessages([])
                setShowSuccess(true)
                if (formikRef.current) formikRef.current.resetForm()
                if (props.closeModal) props.closeModal()

                if (!isUpdating && props.onCreate) {
                    let dataToReturn = response.data
                    dataToReturn.employee = employeeList.find(e => String(e.id) === String(values.employee));
                    dataToReturn.event = eventList.find(e => String(e.id) === String(values.event));
                    if (renderDriver) {
                        dataToReturn.vehicle = vehicleList.find(v => String(v.id) === String((values as any).vehicle));
                    }
                    props.onCreate(dataToReturn)
                }

                if (isUpdating && props.onUpdate) {
                    let dataToReturn = response
                    dataToReturn.employee = employeeList.find(e => String(e.id) === String(values.employee));
                    dataToReturn.event = eventList.find(e => String(e.id) === String(values.event));
                    if (renderDriver) {
                        dataToReturn.vehicle = vehicleList.find(v => String(v.id) === String((values as any).vehicle));
                    }
                    props.onUpdate(dataToReturn)
                }

                if (!props.closeModal && !isUpdating) {
                    navigate('/home')
                }
            }
        } catch (error) {
            setErrorMessages(handlePostError(error, 'Address'))
        }
    }

    if (redirectPath) {
        return <Navigate to={redirectPath}/>;
    }

    return (
        <Container>
            <Row>
                {errorMessages.length > 0 && (
                    <Alert variant="danger">
                        <ul>
                            {errorMessages.map((message, index) => (
                                <li key={index}>{message}</li>
                            ))}
                        </ul>
                    </Alert>
                )}
                {showSuccess && (
                    <Alert variant="success" onClose={() => setShowSuccess(false)} dismissible>
                        Time Entry Successfully Added!
                    </Alert>
                )}
            </Row>

            <Row>
                <Formik<FormValues>
                    enableReinitialize={true}
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={(values, formikHelpers) => {
                        const touchedFields = Object.keys((validationSchema as any).fields).reduce(
                            (acc, field) => ({...acc, [field]: true}),
                            {}
                        );
                        formikHelpers.setTouched(touchedFields);

                        formikHelpers.validateForm().then(errors => {
                            if (Object.keys(errors).length === 0) {
                                handleSubmit(values);
                            }
                        });
                    }}
                    validateOnChange={true}
                    validateOnBlur={true}
                >
                    {formik => (
                        <Form onSubmit={formik.handleSubmit} autoComplete="off" noValidate>
                            {renderDriver ?
                                (DriverAddTime({
                                    formik: formik,
                                    eventSelectRef: eventSelectRef,
                                    employeeSelectRef: employeeSelectRef,
                                    props: props,
                                    allowedToUpdate: allowedToUpdate,
                                    isUpdating: isUpdating,
                                    employeeList: employeeList,
                                    eventList: eventList,
                                    vehicleList: vehicleList,
                                    workOrderList: workOrderList,
                                    selectedWorkOrder: selectedWorkOrder,
                                    setSelectedWorkOrder: setSelectedWorkOrder
                                })) :
                                (AddTime({
                                    formik: formik,
                                    eventSelectRef: eventSelectRef,
                                    employeeSelectRef: employeeSelectRef,
                                    props: props,
                                    allowedToUpdate: allowedToUpdate,
                                    isUpdating: isUpdating,
                                    employeeList: employeeList,
                                    eventList: eventList,
                                    vehicleList: [],
                                    workOrderList: workOrderList,
                                    selectedWorkOrder: selectedWorkOrder,
                                    setSelectedWorkOrder: setSelectedWorkOrder
                                }))
                            }
                        </Form>
                    )}
                </Formik>
            </Row>
        </Container>
    )
}

export default CreateTimeEntry
